import type { DerivationNode } from "../api/types";
import { useCharacter } from "../character";
import { number, source, text } from "../text";
import { Sheet } from "./Sheet";

const operators: Record<string, string> = {
  sum: "Summe",
  product: "Produkt",
  divide: "geteilt",
  round: "gerundet",
  min: "kleinster Wert",
  max: "größter Wert",
  clamp: "begrenzt",
  table: "Tabelle",
  cap: "Deckel",
  set: "festgelegt",
};

function Node(props: { node: DerivationNode; depth: number; currency?: string | null; onNavigate: (key: string) => void }) {
  const { node } = props;
  const { language, amount } = useCharacter();
  const label = text(node.label, language) || operators[node.kind] || node.kind;
  const children = node.children ?? [];
  return (
    <li className={`derivation-node${node.ignored ? " ignored" : ""}`}>
      <div className="derivation-row">
        <span className="derivation-label">
          {node.ref ? (
            <button type="button" className="link" onClick={() => props.onNavigate(node.ref!)}>
              {label}
            </button>
          ) : (
            label
          )}
          {node.formula && <span className="muted"> {node.formula}</span>}
          {node.ignored && <span className="badge">entfällt</span>}
        </span>
        <span className="derivation-value">{props.currency ? amount(props.currency, node.value) : number(node.value)}</span>
      </div>
      {node.source && <div className="source">{source(node.source)}</div>}
      {children.length > 0 && !(node.ref && props.depth > 0) && (
        <ul className="derivation-children">
          {children.map((child, i) => (
            <Node key={i} node={child} depth={props.depth + 1} currency={props.currency} onNavigate={props.onNavigate} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function DerivationSheet(props: { valueKey: string; onClose: () => void; onNavigate: (key: string) => void }) {
  const { value, display, language } = useCharacter();
  const computed = value(props.valueKey);
  if (!computed) return null;
  return (
    <Sheet title={`${text(computed.label, language)}: ${display(computed)}`} onClose={props.onClose}>
      {computed.derivation && (
        <ul className="derivation">
          <Node node={computed.derivation} depth={0} currency={computed.currency} onNavigate={props.onNavigate} />
        </ul>
      )}
      {(computed.notes ?? []).length > 0 && (
        <>
          <h3>Situativ, nicht eingerechnet</h3>
          <ul className="notes">
            {computed.notes!.map((note, i) => (
              <li key={i}>
                {text(note.text, language)} {note.source && <span className="source">{source(note.source)}</span>}
                {(note.derivation?.children ?? []).length > 0 && (
                  <ul className="derivation-children">
                    {note.derivation!.children!.map((child, j) => (
                      <Node key={j} node={child} depth={1} onNavigate={props.onNavigate} />
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Sheet>
  );
}
